import { useState, useEffect } from 'react'
import { useChat } from '../contexts/ChatContext'
import { getActivity, getCallHistory } from '../services/api'
import { formatTime } from '../utils/time'
import { Row, HubSpread, DateDivider, ActivityRow, EmptyState } from '@ofself/shell'
import UserAvatar from './UserAvatar'

function formatShortTime(ts) {
  if (!ts) return ''
  const d = new Date(ts)
  const now = new Date()
  const diff = now - d
  if (diff < 86400000 && d.getDate() === now.getDate()) return formatTime(ts)
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
  if (diff < 7 * 86400000) return days[d.getDay()]
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

function dayLabel(ts) {
  if (!ts) return ''
  const d = new Date(ts)
  const now = new Date()
  const startOf = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime()
  const days = Math.round((startOf(now) - startOf(d)) / 86400000)
  if (days <= 0) return 'Today'
  if (days === 1) return 'Yesterday'
  if (days < 7) return d.toLocaleDateString(undefined, { weekday: 'long' })
  return d.toLocaleDateString(undefined, { month: 'long', day: 'numeric' })
}

export default function ActivityPage({ onNavigate }) {
  const { selectChannel, openThreadById, channels, dmChannels, markActivitySeen, activeHuddles, startHuddle, navigateToMessage } = useChat()
  const [activities, setActivities] = useState([])
  const [callHistory, setCallHistory] = useState([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('all')

  useEffect(() => {
    setLoading(true)
    Promise.all([
      getActivity().then(res => setActivities(res.activities || [])),
      getCallHistory().then(res => setCallHistory(res.calls || [])).catch(() => {}),
    ])
      .catch(err => console.error('Failed to fetch activity:', err))
      .finally(() => setLoading(false))

    return () => markActivitySeen()
  }, [markActivitySeen])

  const filtered = (() => {
    let items = activities
    if (filter === 'dms') items = items.filter(a => a.type === 'dm' || a.is_dm)
    else if (filter === 'mentions') items = items.filter(a => a.type === 'mention')
    else if (filter === 'threads') items = items.filter(a => a.type === 'thread_reply')
    else if (filter === 'reactions') items = items.filter(a => a.type === 'reaction')
    return items
  })()

  async function handleActivityClick(activity) {
    const allChannels = [...(channels || []), ...(dmChannels || [])]
    let channel = allChannels.find(ch => ch.id === activity.channel_id)

    if (!channel) {
      channel = {
        id: activity.channel_id,
        name: activity.channel_name || activity.channel_id,
        is_dm: activity.is_dm || false,
        other_username: activity.is_dm ? activity.username : undefined,
      }
    }

    selectChannel(channel)

    if (activity.type === 'thread_reply' && activity.thread_id) {
      // One path for every surface that holds a reply without its parent
      // (search hits take the same one) — it awaits the parent instead of
      // racing a timer against the channel load.
      openThreadById(activity.channel_id, activity.thread_id, activity.message_id)
    } else if (activity.message_id) {
      navigateToMessage(activity.channel_id, activity.message_id)
    }
  }

  function renderBadge(activity) {
    if (activity.type === 'reaction') {
      return <span className="activity-row__badge-emoji">{activity.emoji}</span>
    }
    if (activity.type === 'thread_reply') {
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
      )
    }
    // A mention wears no badge (owner call 2026-09-15): "mentioned you" is
    // the row's own line, and the @ on the avatar's corner said it twice.
    return null
  }

  const activeCallsList = Object.values(activeHuddles || {})
  // THE FILTERS ARE THE KIT'S HUB COLUMN: each a leaf dividing the column's
  // height beside the page column, its count beneath its name
  const count = (type) => activities.filter(a => a.type === type).length
  const leaves = [
    { id: 'all', name: 'All', meta: [activities.length] },
    { id: 'dms', name: 'DMs', meta: [activities.filter(a => a.type === 'dm' || a.is_dm).length] },
    { id: 'mentions', name: 'Mentions', meta: [count('mention')] },
    { id: 'threads', name: 'Threads', meta: [count('thread_reply')] },
    { id: 'calls', name: 'Calls', meta: [activeCallsList.length + callHistory.length] },
    { id: 'reactions', name: 'Reactions', meta: [count('reaction')] },
  ]

  return (
    <div className="sidebar activity-page">
      <HubSpread leaves={leaves} active={filter} onSelect={setFilter}>
        <ul className="block__body block-scroll list-rows activity-list" style={{ padding: '8px 0 0' }}>
          {loading ? (
            <EmptyState as="li">Loading activity...</EmptyState>
          ) : filter === 'calls' ? (
            <>
              {activeCallsList.length > 0 && (
                <>
                  <div style={{ padding: '4px 10px 6px', fontSize: 'var(--ofself-text-sm)', fontWeight: 'var(--ofself-weight-semibold)', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Active Now
                  </div>
                  {activeCallsList.map(call => {
                    const channelName = call.channel_name || call.huddle?.channel_id?.slice(0, 8) || 'Unknown'
                    const participants = call.huddle?.participants || []
                    const names = participants.map(p => p.username || p.user_id?.slice(0, 6)).join(', ')
                    const allCh = [...(channels || []), ...(dmChannels || [])]
                    const ch = allCh.find(c => c.id === (call.channel_id || call.huddle?.channel_id))
                    return (
                      <Row
                        as="li"
                        key={call.channel_id || call.huddle?.id}
                        className="list-row"
                        onClick={() => ch && selectChannel(ch)}
                      >
                        <div className="activity-icon-wrap" style={{ color: '#4ade80' }}>
                          <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#4ade80', animation: 'huddle-dot-pulse 1.5s ease-in-out infinite' }} />
                        </div>
                        <span className="list-row__body">
                          <span className="list-row__top">
                            <span className="list-row__name">
                              <strong>{ch?.is_dm ? (ch.other_username || channelName) : `#${channelName}`}</strong>
                              <span style={{ fontWeight: 'var(--ofself-weight-normal)', color: '#4ade80', marginLeft: 4, fontSize: 'var(--ofself-text-sm)' }}>Live</span>
                            </span>
                          </span>
                          <span className="list-row__preview">{names || 'No participants yet'}</span>
                        </span>
                        <button
                          style={{ flexShrink: 0, padding: '3px 10px', borderRadius: 5, border: '1px solid #4ade80', background: 'rgba(74,222,128,0.1)', color: '#4ade80', fontSize: 'var(--ofself-text-sm)', fontWeight: 'var(--ofself-weight-semibold)', cursor: 'pointer' }}
                          onClick={e => { e.stopPropagation(); if (ch) { selectChannel(ch); startHuddle() } }}
                        >
                          Join
                        </button>
                      </Row>
                    )
                  })}
                </>
              )}
              <div style={{ padding: '12px 10px 6px', fontSize: 'var(--ofself-text-sm)', fontWeight: 'var(--ofself-weight-semibold)', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Recent Calls
              </div>
              {callHistory.length === 0 ? (
                <EmptyState as="li">No call history yet</EmptyState>
              ) : (
                callHistory.map((call, i) => {
                  const allCh = [...(channels || []), ...(dmChannels || [])]
                  const ch = allCh.find(c => c.id === call.channel_id)
                  const channelLabel = call.is_dm ? (ch?.other_username || 'Direct message') : (call.channel_name ? `#${call.channel_name}` : 'Unknown')
                  const participants = (call.participants || []).map(p =>
                    typeof p === 'object' ? (p.username || p.user_id?.slice(0, 6)) : p
                  )
                  let durStr = ''
                  if (call.duration_seconds != null) {
                    const m = Math.floor(call.duration_seconds / 60)
                    const s = call.duration_seconds % 60
                    durStr = m ? `${m}m${s ? ` ${s}s` : ''}` : `${s}s`
                  }
                  return (
                    <Row
                      as="li"
                      key={call.id || i}
                      className="list-row"
                      onClick={() => ch && selectChannel(ch)}
                    >
                      <div className="activity-icon-wrap">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
                        </svg>
                      </div>
                      <span className="list-row__body">
                        <span className="list-row__top">
                          <span className="list-row__name">
                            <strong>{channelLabel}</strong>
                            {durStr && <span style={{ fontWeight: 'var(--ofself-weight-normal)', color: 'var(--text-muted)', marginLeft: 4 }}>{durStr}</span>}
                          </span>
                          <span className="list-row__time">{formatShortTime(call.ended_at || call.started_at)}</span>
                        </span>
                        <span className="list-row__preview">
                          {participants.length > 0 ? participants.join(', ') : call.started_by || 'Unknown'}
                        </span>
                        {call.group_name && !call.is_dm && (
                          <span className="list-row__preview" style={{ color: 'var(--text-faint)', fontSize: 'var(--ofself-text-sm)' }}>{call.group_name}</span>
                        )}
                      </span>
                    </Row>
                  )
                })
              )}
            </>
          ) : filtered.length === 0 ? (
            <EmptyState as="li">No activity yet</EmptyState>
          ) : (
            (() => {
              const out = []
              let lastDay = null
              filtered.forEach((activity, i) => {
                const day = dayLabel(activity.timestamp)
                if (day && day !== lastDay) {
                  lastDay = day
                  out.push(<DateDivider key={`day-${day}-${i}`}>{day}</DateDivider>)
                }
                const name = activity.username || '?'
                const content = activity.content || activity.message_content || ''
                const channelLabel = activity.is_dm ? 'Direct message' : (activity.channel_name ? `#${activity.channel_name}` : '')
                const typeLabel = activity.type === 'reaction' ? 'reacted to your message'
                  : activity.type === 'thread_reply' ? 'replied in thread'
                  : activity.type === 'dm' ? 'messaged you'
                  : 'mentioned you'
                const badge = renderBadge(activity)
                out.push(
                  <ActivityRow
                    key={`${activity.type}-${activity.message_id || i}-${activity.timestamp}`}
                    avatar={
                      <UserAvatar
                        userID={activity.user_id}
                        sender={{ id: activity.user_id, username: name }}
                        size={32}
                      />
                    }
                    badge={badge}
                    name={name}
                    action={typeLabel}
                    time={formatShortTime(activity.timestamp)}
                    channel={channelLabel}
                    preview={content}
                    onClick={() => handleActivityClick(activity)}
                  />
                )
              })
              return out
            })()
          )}
        </ul>
      </HubSpread>
    </div>
  )
}
